import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    TrendingUp, TrendingDown, DollarSign, BarChart3,
    Calendar, Filter, X, ArrowUpRight, ArrowDownRight,
    PieChart, Download, Users
} from 'lucide-react';
import financeService from '../services/financeService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import toast from 'react-hot-toast'; // Assuming toast is available
import { jsPDF } from 'jspdf'; // Assuming jsPDF is available

const FinanceExpensesAnalysis = () => {
    const { user } = useAuth();
    const canView = hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:view') || hasPermission(user?.role, 'financial:expenses');

    // Estados de filtros
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedStartMonth, setSelectedStartMonth] = useState(1);
    const [selectedEndMonth, setSelectedEndMonth] = useState(12);

    const { data: analysisData, isLoading } = useQuery({
        queryKey: ['expenses-analysis', selectedYear, selectedStartMonth, selectedEndMonth],
        queryFn: () => financeService.getExpensesAnalysis({
            year: selectedYear,
            start_month: selectedStartMonth,
            end_month: selectedEndMonth
        }),
        enabled: canView,
        staleTime: 5 * 60 * 1000 // 5 minutes cache
    });

    const analysis = analysisData?.data || null;

    const handleExportPDF = () => {
        if (!analysis) return;
        try {
            const pdf = new jsPDF();
            pdf.text("Relatório de Despesas", 10, 10);
            // Simple text dump for now, can be improved
            pdf.text(`Total: R$ ${analysis.total.toLocaleString('pt-BR')}`, 10, 20);
            pdf.save(`relatorio-despesas-${selectedYear}.pdf`);
            toast.success("PDF exportado!");
        } catch (e) {
            toast.error("Erro ao exportar PDF");
        }
    };

    if (!canView) {
        return <div className="p-6 text-red-600">Sem permissão para visualizar análise de despesas.</div>;
    }

    return (
        <div className="p-6 space-y-6">
            {/* Cabeçalho */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Análise de Despesas</h1>
                    <p className="text-gray-600">Visão detalhada de saídas financeiras e custos operacionais</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExportPDF}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        disabled={!analysis}
                    >
                        <Download className="w-5 h-5" />
                        Exportar PDF
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-4">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">Filtros</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            {[2024, 2025, 2026, 2027].map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mês Inicial</label>
                        <select
                            value={selectedStartMonth}
                            onChange={(e) => setSelectedStartMonth(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                <option key={month} value={month}>
                                    {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mês Final</label>
                        <select
                            value={selectedEndMonth}
                            onChange={(e) => setSelectedEndMonth(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                <option key={month} value={month}>
                                    {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-end">
                        {/* Reset button logic if needed */}
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="p-6 text-center text-gray-500">Carregando dados...</div>
            ) : !analysis ? (
                <div className="p-6 text-center text-gray-500">Nenhum dado encontrado para o período.</div>
            ) : (
                <>
                    {/* Cards Resumo */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total no Período</p>
                                    <p className="text-2xl font-bold text-red-600">R$ {analysis.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                </div>
                                <DollarSign className="w-8 h-8 text-red-500" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Média Mensal (Período)</p>
                                    <p className="text-2xl font-bold text-orange-600">R$ {analysis.average.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                                </div>
                                <TrendingUp className="w-8 h-8 text-orange-500" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Meses Analisados</p>
                                    <p className="text-2xl font-bold text-blue-600">{selectedEndMonth - selectedStartMonth + 1}</p>
                                </div>
                                <Calendar className="w-8 h-8 text-blue-500" />
                            </div>
                        </div>
                    </div>

                    {/* Gráfico Mensal */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <BarChart3 className="w-6 h-6" />
                            Evolução Mensal
                        </h2>
                        <div className="relative h-96">
                            <div className="absolute inset-0 flex items-end justify-between gap-2 px-4">
                                {analysis.monthly.map((m) => {
                                    const maxVal = Math.max(...analysis.monthly.map(x => x.amount), 1);
                                    const maxHeightPx = 352; // 384px - 32px for labels

                                    // Calculate pixel height with minimum 12px for non-zero values
                                    const calculatedHeight = Math.floor((m.amount / maxVal) * maxHeightPx);
                                    const heightPx = m.amount > 0 ? Math.max(calculatedHeight, 12) : 2;

                                    return (
                                        <div key={m.month} className="flex-1 flex flex-col items-center group relative">
                                            <div
                                                className={`w-full rounded-t transition-all relative ${m.amount > 0
                                                        ? 'bg-red-500 hover:bg-red-600 shadow-sm'
                                                        : 'bg-gray-200 border border-gray-300'
                                                    }`}
                                                style={{ height: `${heightPx}px` }}
                                            >
                                                {/* Hover tooltip */}
                                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-900 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-white px-3 py-1.5 shadow-lg rounded-md border border-gray-300 z-20">
                                                    R$ {m.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </div>

                                                {/* Value label on bar for non-zero values */}
                                                {m.amount > 0 && heightPx >= 24 && (
                                                    <div className="absolute top-1 left-1/2 -translate-x-1/2 text-[10px] font-bold text-white whitespace-nowrap">
                                                        {m.amount >= 1000 ? `${(m.amount / 1000).toFixed(1)}K` : Math.round(m.amount)}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-xs text-gray-600 mt-2 font-medium">
                                                {m.monthName.slice(0, 3)}
                                            </span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-600">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-red-500 rounded shadow-sm"></div>
                                <span>Com despesas</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-gray-200 border border-gray-300 rounded"></div>
                                <span>Sem despesas</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Por Categoria */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <PieChart className="w-6 h-6" />
                                Por Categoria
                            </h2>
                            <div className="space-y-3">
                                {analysis.byCategory.map((cat, idx) => {
                                    const percent = (cat.total / analysis.total) * 100;
                                    return (
                                        <div key={idx} className="relative">
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="font-medium text-gray-700">{cat.category}</span>
                                                <span className="text-gray-900 font-bold">R$ {cat.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({percent.toFixed(1)}%)</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2.5">
                                                <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${percent}%` }}></div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {analysis.byCategory.length === 0 && <p className="text-gray-500">Sem dados.</p>}
                            </div>
                        </div>

                        {/* Por Fornecedor */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-6 h-6" />
                                Top Fornecedores
                            </h2>
                            <div className="space-y-3">
                                {analysis.bySupplier.map((sup, idx) => {
                                    const percent = (sup.total / analysis.total) * 100;
                                    return (
                                        <div key={idx} className="relative">
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="font-medium text-gray-700">{sup.supplier}</span>
                                                <span className="text-gray-900 font-bold">R$ {sup.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2.5">
                                                <div className="bg-orange-500 h-2.5 rounded-full" style={{ width: `${percent}%` }}></div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {analysis.bySupplier.length === 0 && <p className="text-gray-500">Sem dados.</p>}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FinanceExpensesAnalysis;
